import axios, { AxiosError } from "axios";
import { AlertConstants, AlertTypes, useAlerts, useLoading as useLoadingBcr } from "best-common-react";
import React, { useContext, useEffect, useState } from "react";
type LoadingContextType = {
  handleError: (error: Error, fallbackErrorMessage?: string) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  setLoadingKey(key: string, value: boolean): void;
  loadingStatus: React.ReactNode;
  setLoadingStatus: (value: React.ReactNode) => void;
};

const LoadingContext = React.createContext<LoadingContextType | undefined>(undefined);

const LoadingProvider = ({ ...props }) => {
  const { loading, setLoading, label, setLabel } = useLoadingBcr();
  const [loadingObj, setLoadingObj] = useState<{ [key: string]: boolean }>({});
  const { addAlert } = useAlerts();

  const setLoadingDefault = (value: boolean) => {
    setLoadingKey("default", value);
  };

  const setLoadingKey = (key: string, value: boolean) => {
    if (!value) {
      // remove key from loadingObj if loading is false
      setLoadingObj((prev) => {
        const newObj = { ...prev };
        delete newObj[key];
        return newObj;
      });
    } else {
      // add key to loadingObj
      setLoadingObj((prev) => {
        return { ...prev, [key]: value };
      });
    }
  };

  const handleError = (error: Error, fallbackErrorMessage?: string) => {
    let message = fallbackErrorMessage ?? "Unknown error occurred";
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      message = error.response?.data?.message;
    }

    console.error(error);
    addAlert({
      type: AlertConstants.TYPES.DANGER,
      text: message,
    });
  };

  useEffect(() => {
    setLoading(Object.values(loadingObj).some((value) => value));
  }, [loadingObj]);

  return (
    <LoadingContext.Provider
      value={{
        handleError,
        loading,
        setLoading: setLoadingDefault,
        setLoadingKey,
        loadingStatus: label,
        setLoadingStatus: setLabel,
      }}
      {...props}
    />
  );
};

const useLoading = (): LoadingContextType => {
  const context: LoadingContextType | undefined = useContext<LoadingContextType | undefined>(LoadingContext);
  if (context === undefined) {
    throw new Error(`useLoading must be used within a LoadingProvider`);
  }
  return context;
};

export { LoadingProvider, useLoading };
